/** Helpers de data/UI compartilhados. */

import type {
  ColunaKanban,
  EndpointTransicao,
  Etiqueta,
  NomeTransicao,
} from './types';

export const PT_MES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
export const PT_DIA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/** '2026-05-25' → '25/05' ou '25/05/2026'. */
export function fmtData(iso: string, opts: { curto?: boolean } = {}): string {
  const d = new Date(iso + 'T00:00:00');
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return opts.curto ? `${dia}/${mes}` : `${dia}/${mes}/${d.getFullYear()}`;
}

/** ISO datetime '2026-05-25T17:30:00' → '2026-05-25 17:30'. */
export function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 16).replace('T', ' ');
}

/** 'YYYY-Www' da data dada. Usa segunda como início (ISO 8601). */
export function semanaISO(d: Date = new Date()): string {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dow = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dow);
  const ano = t.getUTCFullYear();
  const w = Math.ceil(
    ((t.getTime() - new Date(Date.UTC(ano, 0, 1)).getTime()) / 86400000 + 1) / 7,
  );
  return `${ano}-W${String(w).padStart(2, '0')}`;
}

/** 'YYYY-Www' → [Date(seg), Date(dom)]. */
export function semanaToDatas(iso: string): [Date, Date] {
  const [anoStr, wStr] = iso.split('-W');
  const ano = parseInt(anoStr, 10);
  const w = parseInt(wStr, 10);
  // ISO 8601: semana 1 contém 4 de janeiro
  const jan4 = new Date(Date.UTC(ano, 0, 4));
  const dow = jan4.getUTCDay() || 7;
  jan4.setUTCDate(jan4.getUTCDate() - (dow - 1));
  const seg = new Date(jan4);
  seg.setUTCDate(jan4.getUTCDate() + (w - 1) * 7);
  const dom = new Date(seg);
  dom.setUTCDate(seg.getUTCDate() + 6);
  return [seg, dom];
}

/**
 * Calcula o rótulo 'YYYY-Www' a partir de uma data que já está em UTC.
 * Usado pela navegação (semanaAdjacente): semanaToDatas devolve a segunda em
 * UTC-meia-noite, então NÃO podemos usar getters locais (no fuso BR a UTC-0h
 * vira o dia anterior, e o +7 dias "voltava" pra mesma semana — bug da seta →).
 */
function semanaISOdeUTC(t0: Date): string {
  const t = new Date(t0.getTime());
  const dow = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dow);   // quinta-feira da semana ISO
  const ano = t.getUTCFullYear();
  const w = Math.ceil(
    ((t.getTime() - Date.UTC(ano, 0, 1)) / 86400000 + 1) / 7,
  );
  return `${ano}-W${String(w).padStart(2, '0')}`;
}

/** Próxima semana ISO ou anterior. */
export function semanaAdjacente(iso: string, delta: 1 | -1): string {
  const [seg] = semanaToDatas(iso);          // segunda em UTC
  seg.setUTCDate(seg.getUTCDate() + delta * 7);
  return semanaISOdeUTC(seg);                 // ISO puro em UTC (sem shift de fuso)
}

export function diasAteVencimentoLocal(isoData: string): number {
  const venc = new Date(isoData + 'T00:00:00');
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return Math.floor((venc.getTime() - hoje.getTime()) / 86_400_000);
}

export function colunaDe(etq: Etiqueta): ColunaKanban {
  if (etq === 'vermelho' || etq === 'amarelo') return 'nao_feitos';
  if (etq === 'verde') return 'pronto_protocolo';
  if (etq === 'cinza') return 'concluidos';
  return 'em_andamento';
}

export const VAR_COR: Record<Etiqueta, string> = {
  vermelho:    'var(--etq-vermelho)',
  amarelo:     'var(--etq-amarelo)',
  azul_claro:  'var(--etq-azul-claro)',
  azul_escuro: 'var(--etq-azul-escuro)',
  roxo:        'var(--etq-roxo)',
  laranja:     'var(--etq-laranja)',
  rosa:        'var(--etq-rosa)',
  teal:        'var(--etq-teal)',
  lavanda:     'var(--etq-lavanda)',
  verde:       'var(--etq-verde)',
  cinza:       'var(--etq-cinza)',
};

export const BG_TAILWIND: Record<Etiqueta, string> = {
  vermelho:    'bg-etq-vermelho',
  amarelo:     'bg-etq-amarelo',
  azul_claro:  'bg-etq-azulClaro',
  azul_escuro: 'bg-etq-azulEscuro',
  roxo:        'bg-etq-roxo',
  laranja:     'bg-etq-laranja',
  rosa:        'bg-etq-rosa',
  teal:        'bg-etq-teal',
  lavanda:     'bg-etq-lavanda',
  verde:       'bg-etq-verde',
  cinza:       'bg-etq-cinza',
};

/** Cores de avatar: fundo vibrante + texto contrastante automático. */
export const AVATAR_COR: Record<string, { bg: string; text: string }> = {
  azul_claro:  { bg: '#0ea5e9', text: '#fff'     }, // sky-500 → branco
  azul_escuro: { bg: '#1d4ed8', text: '#fff'     }, // blue-700 → branco
  roxo:        { bg: '#7c3aed', text: '#fff'     }, // violet-600 → branco
  laranja:     { bg: '#ea580c', text: '#fff'     }, // orange-600 → branco
  rosa:        { bg: '#db2777', text: '#fff'     }, // pink-600 → branco
  teal:        { bg: '#0d9488', text: '#fff'     }, // teal-600 → branco
  lavanda:     { bg: '#5c6bc0', text: '#fff'     }, // indigo-400 (GCal Lavender) → branco
  verde:       { bg: '#16a34a', text: '#fff'     }, // green-600 → branco
  vermelho:    { bg: '#dc2626', text: '#fff'     }, // red-600 → branco
  amarelo:     { bg: '#d97706', text: '#fff'     }, // amber-600 → branco
  cinza:       { bg: '#6b7280', text: '#fff'     }, // gray-500 → branco
};

export function detectaMobile(): boolean {
  const forced = localStorage.getItem('viewPref');
  if (forced === 'mobile') return true;
  if (forced === 'desktop') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}


// ─── Fase 8 — Drag-and-drop ───────────────────────────────────────────────


/**
 * Dado um card no estado atual + lista de transições permitidas (vinda do
 * backend pro user logado) + coluna alvo onde o drop aconteceu, decide qual
 * transição corresponde a essa intenção.
 *
 * Retorna o segmento de URL do endpoint (`EndpointTransicao`) ou null se
 * o movimento não é permitido (ou é no-op por já estar na coluna).
 *
 * Mapping:
 *   alvo=nao_feitos        →  desfazer_triagem (de amarelo) / devolver_triagem
 *   alvo=em_andamento      →  assumir (de amarelo) / desfazer_pronto (de verde)
 *   alvo=pronto_protocolo  →  marcar_pronto (de azul) / desfazer_protocolo (de cinza)
 *   alvo=concluidos        →  protocolar (de verde)
 *
 * Quando há múltiplas opções (ex: amarelo arrastado pra nao_feitos), pega
 * a mais "natural" — `desfazer_triagem` antes de `devolver_triagem`.
 */
function saoColunasAdjacentes(a: ColunaKanban, b: ColunaKanban): boolean {
  const ordem: ColunaKanban[] = ['nao_feitos', 'em_andamento', 'pronto_protocolo', 'concluidos'];
  const idxA = ordem.indexOf(a);
  const idxB = ordem.indexOf(b);
  return Math.abs(idxA - idxB) === 1;
}

export function transicaoParaColuna(
  origemEtiqueta: Etiqueta,
  alvo: ColunaKanban,
  permitidas: NomeTransicao[],
): EndpointTransicao | null {
  const cur = colunaDe(origemEtiqueta);
  const have = (n: NomeTransicao) => permitidas.includes(n);

  if (cur === alvo) {
    if (alvo === 'em_andamento' && have('assumir')) {
      return 'assumir';
    }
    return null;  // no-op
  }
  if (!saoColunasAdjacentes(cur, alvo)) return null; // block jumps

  switch (alvo) {
    case 'nao_feitos':
      // De amarelo, prefere undo (zera triado_por).
      if (origemEtiqueta === 'amarelo' && have('desfazer_triagem'))
        return 'desfazer-triagem';
      // De qualquer cor de dono em_andamento → TRIAGEM: desfazer_assumir desvincula o dono
      if (colunaDe(origemEtiqueta) === 'em_andamento' && have('desfazer_assumir'))
        return 'desfazer-assumir';
      if (have('devolver_triagem')) return 'devolver-triagem';
      return null;

    case 'em_andamento':
      // Assumir DIRETO da triagem (vermelho OU amarelo)
      if ((origemEtiqueta === 'amarelo' || origemEtiqueta === 'vermelho') &&
          have('assumir'))
        return 'assumir';
      if (origemEtiqueta === 'verde' && have('desfazer_pronto'))
        return 'desfazer-pronto';
      return null;

    case 'pronto_protocolo':
      if (colunaDe(origemEtiqueta) === 'em_andamento' && have('marcar_pronto'))
        return 'marcar-pronto';
      if (origemEtiqueta === 'cinza' && have('desfazer_protocolo'))
        return 'desfazer-protocolo';
      return null;

    case 'concluidos':
      if (origemEtiqueta === 'verde' && have('protocolar')) return 'protocolar';
      return null;
  }
}


/**
 * Quick-action "avançar 1 etiqueta" — usada pelo swipe horizontal direita
 * em mobile. Escolhe a próxima transição "forward" natural.
 *
 *   vermelho → triar
 *   amarelo  → assumir  (cor decidida no backend)
 *   azul_*   → marcar_pronto
 *   verde    → protocolar
 *   cinza    → (terminal — sem avanço)
 */
export function transicaoAvancar(
  origemEtiqueta: Etiqueta,
  permitidas: NomeTransicao[],
): EndpointTransicao | null {
  const have = (n: NomeTransicao) => permitidas.includes(n);
  switch (origemEtiqueta) {
    case 'vermelho':    return have('triar') ? 'triar' : null;
    case 'amarelo':     return have('assumir') ? 'assumir' : null;
    // qualquer cor de dono (EM ANDAMENTO) avança para "marcar pronto"
    case 'azul_claro':
    case 'azul_escuro':
    case 'roxo':
    case 'laranja':
    case 'rosa':
    case 'teal':        return have('marcar_pronto') ? 'marcar-pronto' : null;
    case 'verde':       return have('protocolar') ? 'protocolar' : null;
    case 'cinza':       return null;
    default:            return null;
  }
}


/**
 * Quick-action "desfazer 1 etiqueta" — usada pelo swipe horizontal esquerda.
 * Escolhe o undo natural daquele estado.
 *
 *   amarelo  → desfazer_triagem
 *   azul_*   → desfazer_assumir
 *   verde    → desfazer_pronto
 *   cinza    → desfazer_protocolo
 *   vermelho → (inicial — sem retroceder)
 */
export function transicaoRetroceder(
  origemEtiqueta: Etiqueta,
  permitidas: NomeTransicao[],
): EndpointTransicao | null {
  const have = (n: NomeTransicao) => permitidas.includes(n);
  switch (origemEtiqueta) {
    case 'vermelho':    return null;
    case 'amarelo':     return have('desfazer_triagem')   ? 'desfazer-triagem'   : null;
    // qualquer cor de dono (EM ANDAMENTO) retrocede para "desfazer assumir"
    case 'azul_claro':
    case 'azul_escuro':
    case 'roxo':
    case 'laranja':
    case 'rosa':
    case 'teal':        return have('desfazer_assumir')   ? 'desfazer-assumir'   : null;
    case 'verde':       return have('desfazer_pronto')    ? 'desfazer-pronto'    : null;
    case 'cinza':       return have('desfazer_protocolo') ? 'desfazer-protocolo' : null;
    default:            return null;
  }
}
